import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '../../../i18n/useTranslation';
import { useAbpPermissions } from '../../../contexts/AbpPermissionsContext';
import { useLicenseSessionsQuery } from '../../../hooks/useLicenseSessionsQuery';
import { licensesManageService } from '../../../api/licensesManageService';
import type { ManageLicenseListDto } from '../../../types/manageLicenses';
import LicenseSuspendAndTerminateDialog from './dialogs/LicenseSuspendAndTerminateDialog';

interface Props {
  license: ManageLicenseListDto;
  onClose: () => void;
}

export default function LicenseDrawerSessionsTab({ license, onClose }: Props) {
  const { t } = useTranslation();
  const { data: sessions, isLoading } = useLicenseSessionsQuery(license.id);
  const [terminatingId, setTerminatingId] = useState<string | null>(null);
  const [showSuspendDialog, setShowSuspendDialog] = useState(false);
  const qc = useQueryClient();
  const { hasPermission } = useAbpPermissions();

  const canSuspendAndTerminate =
    hasPermission('learnleaf.App.SuspendLicense') && hasPermission('learnleaf.App.TerminateSession');

  async function terminate(sessionId: string) {
    setTerminatingId(sessionId);
    try {
      await licensesManageService.terminateSession(sessionId);
      qc.invalidateQueries({ queryKey: ['manage-licenses'] });
      qc.invalidateQueries({ queryKey: ['manage-licenses', 'sessions', license.id] });
    } finally {
      setTerminatingId(null);
    }
  }

  if (isLoading) return <div className="p-4 text-sm text-neutral-500">{t('App.Admin.Licenses.LicenseDrawerSessionsTab.Loading')}</div>;

  return (
    <div className="p-4 flex flex-col gap-4">
      {canSuspendAndTerminate && (
        <button
          type="button"
          onClick={() => setShowSuspendDialog(true)}
          className="px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm self-start"
        >
          {t('App.Admin.Licenses.LicenseDrawerSessionsTab.SuspendAndTerminateButton', { count: sessions?.length ?? 0 })}
        </button>
      )}

      {(sessions?.length ?? 0) === 0 && (
        <p className="text-sm text-neutral-500">{t('App.Admin.Licenses.LicenseDrawerSessionsTab.NoSessions')}</p>
      )}

      {(sessions?.length ?? 0) > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left border-b border-neutral-200">
                <th className="py-1.5 pr-2 font-medium text-neutral-600">{t('App.Admin.Licenses.LicenseDrawerSessionsTab.ColDevice')}</th>
                <th className="py-1.5 pr-2 font-medium text-neutral-600">{t('App.Admin.Licenses.LicenseDrawerSessionsTab.ColIP')}</th>
                <th className="py-1.5 pr-2 font-medium text-neutral-600">{t('App.Admin.Licenses.LicenseDrawerSessionsTab.ColStarted')}</th>
                <th className="py-1.5 pr-2 font-medium text-neutral-600">{t('App.Admin.Licenses.LicenseDrawerSessionsTab.ColLastActivity')}</th>
                <th className="py-1.5 pr-2 font-medium text-neutral-600">{t('App.Admin.Licenses.LicenseDrawerSessionsTab.ColChunks')}</th>
                <th className="py-1.5"></th>
              </tr>
            </thead>
            <tbody>
              {sessions?.map((s) => (
                <tr key={s.id} className="border-t border-neutral-100">
                  <td className="py-1.5 pr-2 font-mono">{s.deviceFingerprintHash.slice(0, 12)}…</td>
                  <td className="py-1.5 pr-2">{s.ipAddress ?? '—'}</td>
                  <td className="py-1.5 pr-2">{new Date(s.startedAt).toLocaleString()}</td>
                  <td className="py-1.5 pr-2">
                    {new Date(s.lastValidatedAt ?? s.startedAt).toLocaleString()}
                  </td>
                  <td className="py-1.5 pr-2">{s.chunksAccessed}</td>
                  <td className="py-1.5">
                    <button
                      type="button"
                      disabled={terminatingId === s.id}
                      onClick={() => terminate(s.id)}
                      className="px-2 py-0.5 border border-red-300 text-red-700 rounded hover:bg-red-50 disabled:opacity-50 text-xs"
                    >
                      {terminatingId === s.id ? t('App.Admin.Licenses.LicenseDrawerSessionsTab.Terminating') : t('App.Admin.Licenses.LicenseDrawerSessionsTab.Terminate')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <LicenseSuspendAndTerminateDialog
        open={showSuspendDialog}
        license={license}
        activeSessionCount={sessions?.length ?? 0}
        onClose={() => setShowSuspendDialog(false)}
        onCompleted={() => {
          setShowSuspendDialog(false);
          onClose();
        }}
      />
    </div>
  );
}
